import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { captureException } from '@sentry/browser';
import { CitationReportFromApi, MiniReportData } from './types';

const TRANSIENT_ERROR_CODES = [502, 503, 504];

async function fetchWithRetry(
  fetcher: () => Promise<Response>,
  maxRetries = 8,
  maxDelay = 30000,
): Promise<Response> {
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetcher();
      lastResponse = response;

      if (response.ok) {
        return response;
      }

      // If it's a transient error and we have retries left, retry with exponential backoff
      if (
        TRANSIENT_ERROR_CODES.includes(response.status) &&
        attempt < maxRetries
      ) {
        const delay = Math.min(Math.pow(2, attempt) * 3000, maxDelay); // 3s, 6s, 12s, 24s, 30s
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Non-transient HTTP error - return immediately for caller to handle
      return response;
    } catch (error) {
      // Network error - retry if attempts left, otherwise log and throw
      if (attempt < maxRetries) {
        const delay = Math.min(Math.pow(2, attempt) * 3000, maxDelay);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Log to Sentry and throw user-friendly error after all retries exhausted
      captureException(error);
      throw new Error('Network error - please reload the page to try again.');
    }
  }

  // Defensive fallback - should never reach here
  if (lastResponse) {
    return lastResponse;
  }
  throw new Error('Failed to fetch after retries');
}

export const loadDocument = async (
  documentId?: string,
): Promise<{ document: string; document_id: string } | null> => {
  const params: { subdomain_param: string; document_id?: string } = {
    subdomain_param: 'api',
  };
  if (documentId) {
    params.document_id = documentId;
  }

  const url = routes.api_v0_citeright_get_document_path(params);

  const response = await fetchWithCsrf(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  // Handle 404 gracefully - no document exists yet
  if (response.status === 404) {
    return null;
  }

  // Handle redirect response
  if (response.status === 303) {
    const data = await response.json();
    if (data.redirect_to) {
      window.location.href = data.redirect_to;
      return null;
    }
  }

  if (!response.ok) {
    throw new Error(`Load failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Updates the search query for a specific claim in a citation report
 * @param reportId - ID of the citation report containing the claim
 * @param claimId - ID of the claim to update
 * @param newQuery - New search query text (max 1000 characters)
 * @returns Promise resolving to the updated citation report data
 */
export const updateQuery = async (
  reportId: string,
  claimId: string,
  newQuery: string,
  publicToken?: string,
) => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_update_query_path({
      subdomain_param: 'api',
    }),
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(publicToken
          ? { public_token: publicToken }
          : { report_id: reportId }),
        claim_id: claimId,
        new_query: newQuery,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Update query failed: ${response.statusText}`);
  }

  return response.json();
};

export const addClaim = async (
  reportId: string,
  claimText: string,
  publicToken?: string,
) => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_add_query_path(),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(publicToken
          ? { public_token: publicToken }
          : { report_id: reportId }),
        text: claimText,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Add claim failed: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Triggers a search for citations for a specific claim
 * @param reportId - ID of the citation report containing the claim
 * @param claimId - ID of the claim to search for
 * @returns Promise resolving to the updated citation report data
 */
export const searchClaim = async (
  reportId: string,
  claimId: string,
  publicToken?: string,
) => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_search_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(publicToken
          ? { public_token: publicToken }
          : { report_id: reportId }),
        claim_id: claimId,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Search claim failed: ${response.statusText}`);
  }

  return response.json();
};

export const createCitationReport = async (
  text: string,
  rawFile?: File,
): Promise<{ report: CitationReportFromApi }> => {
  const formData = new FormData();
  formData.append('document_text', text);
  if (rawFile) {
    formData.append('document', rawFile);
  }
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_citation_report_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error(`Start citation report failed: ${response.statusText}`);
  }

  return response.json();
};

export const createReportFromUpload = async (
  file: File,
  uploadSource?: 'mini_report' | 'landing',
): Promise<{ report: CitationReportFromApi }> => {
  const formData = new FormData();
  formData.append('document', file);
  if (uploadSource) {
    formData.append('upload_source', uploadSource);
  }

  const response = await fetchWithCsrf(
    routes.api_v0_citeright_create_report_from_upload_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error(`Create report from upload failed: ${response.statusText}`);
  }

  return response.json();
};

export const createReportFromWork = async (
  workId: string,
): Promise<{ report: CitationReportFromApi }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_create_report_from_work_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        work_id: workId,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create report from work');
  }

  return response.json();
};

export const fetchMiniReport = async (
  miniReportId: string,
): Promise<{ mini_report: MiniReportData; user_id: number }> => {
  const response = await fetchWithRetry(() =>
    fetchWithCsrf(
      routes.api_v0_citeright_mini_report_path({
        subdomain_param: 'api',
        mini_report_id: miniReportId,
      }),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    ),
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    // Use generic error for 404 and all other status codes
    throw new Error('This mini report is not available.');
  }

  return response.json();
};

export const startCitationReport = async (
  reportId: number,
): Promise<{ report: CitationReportFromApi }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_start_citation_report_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report_id: reportId }),
    },
  );

  if (!response.ok) {
    throw new Error(`Start citation report failed: ${response.statusText}`);
  }

  return response.json();
};

export const recordMilestone = async (
  reportId: number,
  milestone: string,
  claimId?: string,
  options?: { keepalive?: boolean; publicToken?: string },
): Promise<void> => {
  const fetcher = options?.publicToken ? fetch : fetchWithCsrf;
  const body: Record<string, unknown> = {
    report_id: reportId,
    milestone,
    claim_id: claimId,
  };
  if (options?.publicToken) {
    body.public_token = options.publicToken;
  }

  const response = await fetcher(
    routes.api_v0_citeright_record_milestone_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: options?.keepalive,
    },
  );

  if (!response.ok) {
    throw new Error(`Record milestone failed: ${response.statusText}`);
  }
};

export const getCitationReport = async (
  reportId?: string,
  publicToken?: string,
): Promise<{ report: CitationReportFromApi }> => {
  const pathParams: Record<string, string> = { subdomain_param: 'api' };
  if (publicToken) {
    pathParams.public_token = publicToken;
  } else if (reportId) {
    pathParams.report_id = reportId;
  }

  const fetcher = publicToken ? fetch : fetchWithCsrf;

  const response = await fetchWithRetry(() =>
    fetcher(routes.api_v0_citeright_citation_report_path(pathParams)),
  );

  if (!response.ok) {
    throw new Error(
      publicToken
        ? 'Report not available'
        : 'Failed to fetch your latest report data. Try refreshing the page if your report is incomplete.',
    );
  }

  return response.json();
};

export const updateCiterightConfig = async (
  reportId: number | undefined,
  scope: 'global' | 'user',
  updates: { [key: string]: unknown },
): Promise<{ message: string }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_update_config_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        report_id: reportId,
        scope: scope,
        updates: updates,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update configuration');
  }

  return response.json();
};

export type PaperEvaluationChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type PaperEvaluationChatResponse = {
  chat_id: string;
  conversation: PaperEvaluationChatMessage[];
  openalex_work_id: string;
};

export const initializePaperEvaluationChat = async (
  openalexWorkId: string,
  title?: string,
  abstract?: string,
): Promise<PaperEvaluationChatResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_paper_evaluation_chats_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        openalex_work_id: openalexWorkId,
        title,
        abstract,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Initialize chat failed: ${response.statusText}`);
  }

  return response.json();
};

export const sendPaperEvaluationChatMessage = async (
  chatId: string,
  message: string,
  title?: string,
  abstract?: string,
): Promise<PaperEvaluationChatResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_paper_evaluation_chat_messages_path({
      subdomain_param: 'api',
      chat_id: chatId,
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        title,
        abstract,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Send message failed: ${response.statusText}`);
  }

  return response.json();
};

export const getPaperEvaluationChatHistory = async (
  chatId: string,
): Promise<PaperEvaluationChatResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_paper_evaluation_chat_history_path({
      subdomain_param: 'api',
      chat_id: chatId,
    }),
  );

  if (!response.ok) {
    throw new Error(`Get chat history failed: ${response.statusText}`);
  }

  return response.json();
};

export const addReferencesToDoc = async (
  reportId: number,
  works: Array<{
    title: string;
    authors: Array<{ name: string }>;
    publication?: string;
    publication_year?: number;
    publisher?: string;
    doi?: string;
    url?: string;
    abstract?: string;
    work_id: string;
    claim_id?: string;
    claim_text?: string;
  }>,
): Promise<{ status: string; report_id: number }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_add_references_to_doc_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report_id: reportId, works }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to add references to document: ${response.statusText}`,
    );
  }

  return response.json();
};

export const getDocumentWithReferencesStatus = async (
  reportId: number,
): Promise<{ ready: boolean; url?: string; filename?: string }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_document_with_references_status_path({
      subdomain_param: 'api',
      report_id: reportId,
    }),
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to check document status: ${response.statusText}`,
    );
  }

  return response.json();
};

// ============================================================================
// Reference Evaluation API
// ============================================================================

export type ReferenceEvaluationStatus =
  | 'pending'
  | 'processing_grobid'
  | 'extracting_references'
  | 'validating'
  | 'extracting_claims'
  | 'evaluating'
  | 'completed'
  | 'error';

export type ClaimJudgement =
  | 'supported'
  | 'not_supported'
  | 'maybe_supported'
  | 'partially_supported'
  | 'could_not_find_support';

export type ClaimEvaluation = {
  claim_text: string;
  reasoning: string;
  quote: string | null;
  supported: ClaimJudgement;
};

export type ReferenceData = {
  id: string;
  identifier: string | null;
  title: string;
  authors: string[];
  publication_year: number | null;
  publication: string | null;
  doi: string | null;
  pages: string | null;
  volume: string | null;
  issue: string | null;
  url: string | null;
  publisher: string | null;
  verified: boolean;
  openalex_id: string | null;
  citation_count: number | null;
  abstract: string | null;
  impact_factor: number | null;
  journal_quartile: string | null;
  journal_rank: string | null;
  claims: string[];
  raw_reference: string | null;
};

export type ReferenceEvaluationData = {
  full_text_url: string | null;
  claims: ClaimEvaluation[];
};

export type ReferenceMetadata = {
  doi: string | null;
  title: string | null;
  authors: string[] | null;
  publication_year: number | null;
  publication: string | null;
};

export type MetadataValidation = {
  doi_format_valid: boolean | null;
  doi_resolves: boolean | null;
  doi_matches?: boolean | null;
  title_similarity: number | null;
  title_similarity_threshold_met: boolean | null;
  authors_match: boolean | null;
  year_matches: boolean | null;
  publication_matches: boolean | null;
  overall_valid: boolean;
};

export type ReferenceEvaluationResult = {
  reference_id: string;
  reference_title: string;
  full_text_url: string | null;
  best_source_url: string | null;
  error?: string;
  claims: ClaimEvaluation[];
  found_metadata?: ReferenceMetadata;
  metadata_source?: 'openalex' | 'web';
  validation?: MetadataValidation;
};

export type WorkContext = {
  title: string | null;
  abstract: string | null;
  authors: string[];
};

export type ReferenceEvaluationFromApi = {
  id: number;
  status: ReferenceEvaluationStatus;
  current_step: string | null;
  error_message: string | null;
  references: ReferenceData[];
  claims_to_references: Array<{ text: string; citations: string[] }>;
  evaluations: ReferenceEvaluationResult[];
  references_count: number;
  verified_references_count: number;
  evaluations_count: number;
  created_at: string;
  updated_at: string;
  work_context: WorkContext | null;
};

/**
 * Creates a new reference evaluation by uploading a document
 */
export const createReferenceEvaluation = async (
  file: File,
): Promise<{ evaluation: ReferenceEvaluationFromApi }> => {
  const formData = new FormData();
  formData.append('document', file);

  const response = await fetchWithCsrf(
    routes.api_v0_reference_evaluation_create_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      body: formData,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to create reference evaluation: ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Gets the current status and results of a reference evaluation
 */
export const getReferenceEvaluation = async (
  evaluationId: number,
): Promise<{ evaluation: ReferenceEvaluationFromApi }> => {
  const response = await fetchWithRetry(() =>
    fetchWithCsrf(
      routes.api_v0_reference_evaluation_show_path({
        subdomain_param: 'api',
        id: evaluationId,
      }),
    ),
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to get reference evaluation: ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Lists all reference evaluations for the current user
 */
export const listReferenceEvaluations = async (): Promise<{
  evaluations: ReferenceEvaluationFromApi[];
}> => {
  const response = await fetchWithCsrf(
    routes.api_v0_reference_evaluation_index_path({
      subdomain_param: 'api',
    }),
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        `Failed to list reference evaluations: ${response.statusText}`,
    );
  }

  return response.json();
};

// ============================================================================
// Report List API
// ============================================================================

export type ReportStatus =
  | 'completed'
  | 'processing'
  | 'expired'
  | 'error'
  | 'pending';

export type ReportListItem = {
  id: number;
  title: string;
  file_type: string;
  status: ReportStatus;
  updated_at: string;
  has_redis_data: boolean;
};

export type ReportListPagination = {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_more: boolean;
};

export type ListReportsResponse = {
  reports: ReportListItem[];
  pagination: ReportListPagination;
};

/**
 * Lists citation reports for the current user with pagination
 * @param page - Page number (default: 1)
 * @param perPage - Number of items per page (default: 10)
 * @returns Promise resolving to paginated list of reports
 */
export const listReports = async (
  page = 1,
  perPage = 10,
): Promise<ListReportsResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_citeright_list_reports_path({
      subdomain_param: 'api',
      page: page.toString(),
      per_page: perPage.toString(),
    }),
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to list reports: ${response.statusText}`,
    );
  }

  return response.json();
};
